/**
 * Song info of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 "SONG INFO", §5.1 step 2): the song
 * file, its length, format, sample rate and channels, whether Phira can play it, the title and artist (edited in
 * the project settings), and notes that matter for timing (MP3 decoder delay, the streaming playback path).
 */

import type { AppServices } from '../../app/registry/types';
import type { MusicInfo } from '../../audio/types';
import { formatSec } from '../../core/time/beatSyntax';
import { Button, Icon } from '../../ui';
import { m } from './timing.messages';
import type { TimingView } from './timingDoc';
import type { TimingText } from './timingHooks';
import s from './Timing.module.css';

export interface SongInfoSectionProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
  readonly music: MusicInfo | null;
  /** Length from the analysis (s), when the engine has not decoded the song. */
  readonly analysedDurationSec: number | null;
}

function fileName(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? path : path.slice(i + 1);
}

export function SongInfoSection(props: SongInfoSectionProps) {
  const { services, text, view } = props;
  const ref = view.music;
  const meta = ref?.meta;
  const decoded = props.music !== null && ref !== null && props.music.asset === ref.id ? props.music : null;
  const duration = decoded?.durationSec ?? meta?.durationSec ?? props.analysedDurationSec;
  const sampleRate = decoded?.sampleRate ?? meta?.sampleRate ?? null;
  const channels = decoded !== null && decoded.channels > 0 ? decoded.channels : (meta?.channels ?? null);
  const codec = meta?.codec ?? null;
  const decodable = meta?.phiraDecodable;
  const unknown = text.t(m.infoUnknown);
  const rows: Array<readonly [string, string]> = [
    [text.t(m.infoTitle), view.title === '' ? unknown : view.title],
    [text.t(m.infoArtist), view.artist === '' ? unknown : view.artist],
    [text.t(m.infoFile), ref === null ? unknown : fileName(ref.path)],
    [text.t(m.infoDuration), duration === null ? unknown : formatSec(duration)],
    [text.t(m.infoFormat), codec === null ? (ref?.mime ?? unknown) : codec.toUpperCase()],
    [
      text.t(m.infoSampleRate),
      sampleRate === null ? unknown : text.t(m.infoSampleRateValue, { khz: sampleRate / 1000 }),
    ],
    [text.t(m.infoChannels), channels === null ? unknown : text.t(m.infoChannelsValue, { count: channels })],
  ];
  return (
    <div className={s.stack}>
      <dl className={s.stack} style={{ margin: 0 }}>
        {rows.map(([k, v]) => (
          <div key={k} className={s.row}>
            <dt className={s.label} style={{ minWidth: '7em' }}>
              {k}
            </dt>
            <dd className={s.mono} style={{ margin: 0 }}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
      {decodable !== undefined && (
        <p className={s.row} style={{ margin: 0 }}>
          <Icon name={decodable ? 'CircleCheck' : 'TriangleAlert'} size={14} />
          <span>{text.t(decodable ? m.infoPhiraOk : m.infoPhiraNo)}</span>
        </p>
      )}
      {codec === 'mp3' && <p className={s.help}>{text.t(m.infoMp3)}</p>}
      {decoded?.streaming === true && <p className={s.help}>{text.t(m.infoStreaming)}</p>}
      <div className={s.row}>
        <Button
          size="sm"
          icon="Music"
          onClick={() => {
            void services.registry.runAny('project.settings', undefined, 'button');
          }}
        >
          {text.t(m.infoEdit)}
        </Button>
      </div>
    </div>
  );
}
